import React from 'react';
import { CVSSSeverity } from '../../types/finding';

interface CVSSScoreBadgeProps {
  score: number;
  severity?: CVSSSeverity;
  vector?: string;
  compact?: boolean;
}

const cvssConfig: Record<string, { label: string; bgClass: string; textClass: string; borderClass: string }> = {
  none: {
    label: 'None',
    bgClass: 'bg-ui-background-tertiary',
    textClass: 'text-ui-text-tertiary',
    borderClass: 'border-ui-border-secondary',
  },
  low: {
    label: 'Low',
    bgClass: 'bg-semantic-success/10',
    textClass: 'text-semantic-success',
    borderClass: 'border-semantic-success/20',
  },
  medium: {
    label: 'Medium',
    bgClass: 'bg-semantic-warning/10',
    textClass: 'text-semantic-warning',
    borderClass: 'border-semantic-warning/20',
  },
  high: {
    label: 'High',
    bgClass: 'bg-orange-500/10',
    textClass: 'text-orange-500',
    borderClass: 'border-orange-500/20',
  },
  critical: {
    label: 'Critical',
    bgClass: 'bg-semantic-error/10',
    textClass: 'text-semantic-error',
    borderClass: 'border-semantic-error/20',
  },
};

const getSeverityFromScore = (score: number): string => {
  if (score === 0) return 'none';
  if (score <= 3.9) return 'low';
  if (score <= 6.9) return 'medium';
  if (score <= 8.9) return 'high';
  return 'critical';
};

const CVSSScoreBadge: React.FC<CVSSScoreBadgeProps> = ({
  score,
  severity,
  vector,
  compact = false,
}) => {
  const severityKey = severity?.toLowerCase() || getSeverityFromScore(score);
  const config = cvssConfig[severityKey] || cvssConfig.medium;

  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium border ${config.bgClass} ${config.textClass} ${config.borderClass}`}
      title={vector ? `CVSS Vector: ${vector}` : undefined}
    >
      <span className="font-semibold">{score.toFixed(1)}</span>
      {!compact && <span className="opacity-80">{config.label}</span>}
    </span>
  );
};

export default CVSSScoreBadge;
